/**
 * Microsoft Clarity loader.
 *
 * Clarity is loaded lazily, ONLY after the user has accepted analytics
 * cookies (see `src/components/CookieConsent.tsx`), and NEVER on a route
 * that shows applicant or voucher PII — see `isBlockedClarityRoute` below
 * (#624). The exact script tag shipped is the one Microsoft provides in the
 * Clarity dashboard.
 *
 * Project ID: wya0twtk68 (DigaVouch)
 *
 * Operator pages that opt in to Clarity (call `initClarity()` on mount):
 *   - Verification Center  → src/pages/Dashboard.tsx
 *   - Recruiting queue     → src/pages/RecruitingDashboard.tsx
 *
 * Applicant- and voucher-facing pages (`Applicant.tsx`, `ApplicantFlow.tsx`,
 * `VoucherFlow.tsx`, `DemoApplicant.tsx`, and their /demo mirrors) still call
 * `initClarity()` on mount, matching the routing those pages don't own, but
 * the route guard inside `initClarity()` refuses to load on any of them —
 * those people never chose Clarity as a vendor and are not the customer's
 * staff. That refusal is enforced centrally so it survives a page's own call
 * site changing.
 *
 * Other routes (login, reset-password, cookie policy, etc.) are not
 * instrumented. See docs/microsoft-clarity-and-cookie-policy.md.
 */

const CLARITY_PROJECT_ID = "wya0twtk68";
export const CONSENT_STORAGE_KEY = "dv-cookie-consent-v1";

/**
 * Routes that show applicant or voucher PII (names, dates of birth, evidence
 * summaries) to the person the data belongs to, or to someone reviewing it
 * before that person has any relationship with Clarity as a vendor. Clarity
 * never loads on these, full stop — see #624. Checked against
 * `window.location.pathname` inside initClarity() itself, not left to each
 * page to remember, so the guard survives a future page forgetting to call
 * (or wrongly calling) initClarity().
 */
const BLOCKED_ROUTE_PATTERNS: RegExp[] = [
  /^\/verify\//, // ApplicantFlow — magic-link applicant flow
  /^\/voucher-flow(\/|$)/, // VoucherFlow
  /^\/start(\/|$)/, // StartPage: the applicant types their email address there
  /^\/applicant\/[^/]+/, // Applicant review (operator-viewed, but applicant PII)
  /^\/demo\/applicant-flow/, // Demo mirror of ApplicantFlow
  /^\/demo-new\/applicant-flow/, // Demo mirror of the definition-driven flow
  /^\/demo\/voucher-flow/, // Demo mirror of VoucherFlow
  /^\/demo\/applicant\//, // Demo mirror of Applicant review
  /^\/article(\/|$)/, // Article walkthrough: job posting, application, both flows
  /^\/feather(\/|$)/, // second partner walkthrough: showcase, both flows, ATS page
];

export function isBlockedClarityRoute(pathname: string): boolean {
  return BLOCKED_ROUTE_PATTERNS.some((p) => p.test(pathname));
}

function clarityFn(): ((...args: unknown[]) => void) | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const c = (window as any).clarity;
  return typeof c === "function" ? c : null;
}

let stoppedForRoute = false;

/**
 * Once Clarity is injected on an allowed route (e.g. the operator lands on
 * `/dashboard`), the running session keeps recording through client-side
 * (no full-reload) navigation — `initClarity()`'s route check only runs at
 * injection time, so it can't by itself stop a session that's already going
 * when React Router navigates into a blocked route (operator clicking a case
 * row: `/dashboard` → `/applicant/:id`). Call this on every route change
 * (see `useClarityRouteSync` in AppShell) to pause/resume the live session
 * with Clarity's own `stop`/`start` commands.
 */
export function syncClarityWithRoute(pathname: string) {
  if (typeof window === "undefined") return;
  if (!injected) return;
  const clarity = clarityFn();
  if (!clarity) return;

  const blocked = isBlockedClarityRoute(pathname);
  if (blocked && !stoppedForRoute) {
    stoppedForRoute = true;
    clarity("stop");
  } else if (!blocked && stoppedForRoute) {
    stoppedForRoute = false;
    clarity("start");
  }
}

export type ConsentValue = "granted" | "denied";

export function getStoredConsent(): ConsentValue | null {
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    return v === "granted" || v === "denied" ? v : null;
  } catch {
    return null;
  }
}

export function setStoredConsent(v: ConsentValue) {
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, v);
  } catch {
    /* ignore */
  }
}

let injected = false;

/**
 * Inject Clarity if (a) consent is granted and (b) it hasn't been
 * injected already this page-load. Safe to call repeatedly.
 */
export function initClarity() {
  if (typeof window === "undefined") return;
  if (injected) return;
  if (isBlockedClarityRoute(window.location.pathname)) return;
  if (getStoredConsent() !== "granted") return;

  injected = true;
  // Verbatim Microsoft Clarity snippet.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (function (c: any, l: Document, a: string, r: string, i: string) {
    c[a] =
      c[a] ||
      function (...args: unknown[]) {
        (c[a].q = c[a].q || []).push(args);
      };
    const t = l.createElement(r) as HTMLScriptElement;
    t.async = true;
    t.src = "https://www.clarity.ms/tag/" + i;
    const y = l.getElementsByTagName(r)[0];
    y.parentNode?.insertBefore(t, y);
  })(window, document, "clarity", "script", CLARITY_PROJECT_ID);
}
